import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, MailIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { DEMO_CODE } from '../../contexts/AuthContext';
interface AuthDialogProps {
  /** Nom du module demandé, affiché en contexte. */
  moduleLabel?: string;
  onClose: () => void;
  /** Appelé une fois le code confirmé, avec le courriel ou numéro utilisé. */
  onSuccess: (identifier: string) => void;
}
type Step = 'choix' | 'identifiant' | 'code' | 'succes';
type Mode = 'connexion' | 'inscription';
const ease = [0.23, 1, 0.32, 1] as const;
const primaryButton = 'w-full rounded-full bg-ville-navy px-6 py-3.5 text-[15px] font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:hover:translate-y-0';
const secondaryButton = 'w-full rounded-full border border-slate-200 px-6 py-3.5 text-[15px] font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function AuthDialog({
  moduleLabel,
  onClose,
  onSuccess
}: AuthDialogProps) {
  const [step, setStep] = useState<Step>('choix');
  const [mode, setMode] = useState<Mode>('connexion');
  const [identifier, setIdentifier] = useState('');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const identifierRef = useRef<HTMLInputElement | null>(null);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  useEffect(() => {
    if (step === 'identifiant') identifierRef.current?.focus();
    if (step === 'code') codeRefs.current[0]?.focus();
  }, [step]);
  const start = (nextMode: Mode) => {
    setMode(nextMode);
    setError(null);
    setStep('identifiant');
  };
  const sendCode = () => {
    if (identifier.trim().length < 5) {
      setError('Entrez un courriel ou un numéro de mobile valide.');
      return;
    }
    setError(null);
    setDigits(['', '', '', '', '', '']);
    setStep('code');
  };
  const verify = (value: string[]) => {
    if (value.join('') !== DEMO_CODE) {
      setError('Code invalide. Réessayez.');
      setDigits(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
      return;
    }
    setError(null);
    setStep('succes');
    window.setTimeout(() => onSuccess(identifier.trim()), 700);
  };
  const setDigit = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '');
    if (!value) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }
    const next = [...digits];
    value.split('').forEach((char, offset) => {
      if (index + offset < 6) next[index + offset] = char;
    });
    setDigits(next);
    const lastFilled = Math.min(index + value.length, 5);
    codeRefs.current[lastFilled]?.focus();
    if (next.every(digit => digit !== '')) verify(next);
  };
  return <div className="fixed inset-0 z-[120] flex items-end justify-center p-3 sm:items-center sm:p-6" data-id="element-330" data-mp="2cwvi4lz4nwu4">
      <motion.button type="button" aria-label="Fermer" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="absolute inset-0 h-full w-full cursor-default bg-ville-navy/40 focus:outline-none" data-id="element-331" data-mp="2qwn2d4g8m9gk" />

      <motion.div role="dialog" aria-modal="true" aria-labelledby="auth-titre" initial={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.24,
      ease
    }} className="relative w-full max-w-[440px] rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-ville-navy/20 sm:p-8" data-id="element-332" data-mp="rmyfi7hxyzck">
        <div className="flex items-start justify-between gap-4" data-id="element-333" data-mp="acayfj7byuja">
          <div className="flex items-center gap-2" data-id="element-334" data-mp="fa321ahc853b">
            {step !== 'choix' && step !== 'succes' && <button type="button" onClick={() => {
            setError(null);
            setStep(step === 'code' ? 'identifiant' : 'choix');
          }} aria-label="Étape précédente" className="grid h-9 w-9 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-335" data-mp="k8xhq3k0ddj7">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-336" data-mp="3g6devwb34ojt" />
              </button>}
            <span className="grid h-9 w-9 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-337" data-mp="3b6itg8a2wr65">
              <ShieldCheckIcon className="h-4 w-4" data-id="element-338" data-mp="326ly5nt6ou24" />
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-339" data-mp="2xg5jy1hjh4ep">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-340" data-mp="2iid232ois3xc" />
          </button>
        </div>

        {step === 'choix' && <div className="mt-4" data-id="element-341" data-mp="2ejnkexwg807w">
            <h2 id="auth-titre" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-342" data-mp="23vwlpagzhsal">
              Connexion requise
            </h2>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-343" data-mp="28t678f195jt5">
              {moduleLabel ? `Connectez-vous pour accéder à ${moduleLabel}.` : 'Connectez-vous pour accéder à vos services citoyens.'}
            </p>
            <div className="mt-6 space-y-3" data-id="element-344" data-mp="2njn91zhsubqk">
              <button type="button" onClick={() => start('connexion')} className={primaryButton} data-id="element-345" data-mp="2nkymys4ykzve">
                J’ai déjà un compte
              </button>
              <button type="button" onClick={() => start('inscription')} className={secondaryButton} data-id="element-346" data-mp="2duvhvtkkbvgp">
                Inscription
              </button>
            </div>
          </div>}

        {step === 'identifiant' && <form className="mt-4" onSubmit={event => {
        event.preventDefault();
        sendCode();
      }} data-id="element-347" data-mp="36hgcr408ypp0">
            <h2 id="auth-titre" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-348" data-mp="3knrogmcw1rz7">
              {mode === 'inscription' ? 'Créer mon compte' : 'Se connecter'}
            </h2>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-349" data-mp="3uemcnw9p31na">
              Entrez votre courriel ou votre numéro de mobile. Nous vous envoyons un code à 6 chiffres.
            </p>

            <label htmlFor="auth-identifiant" className="mt-6 block text-sm font-bold text-ville-navy" data-id="element-350" data-mp="31pfeiye5ke2k">
              Courriel ou numéro de mobile
            </label>
            <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-351" data-mp="2xq6mbxyrgo94">
              <MailIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-352" data-mp="36n76ttipm27a" />
              <input id="auth-identifiant" ref={identifierRef} type="text" inputMode="email" autoComplete="email" value={identifier} onChange={event => setIdentifier(event.target.value)} className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-353" data-mp="qwckja5rzjbw" />
            </div>

            {error && <p className="mt-3 text-sm font-semibold text-red-600" data-id="element-354" data-mp="2u6g0l89gneql">{error}</p>}

            <button type="submit" className={`mt-6 ${primaryButton}`} data-id="element-355" data-mp="2z5sirfpdzxmu">
              Recevoir mon code
            </button>
          </form>}

        {step === 'code' && <div className="mt-4" data-id="element-356" data-mp="385xqjqyhsy1j">
            <h2 id="auth-titre" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-357" data-mp="385pa5p35vall">
              Entrez votre code
            </h2>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-358" data-mp="3d2xu9v7vdoph">
              Code envoyé à <span className="font-bold text-ville-navy" data-id="element-359" data-mp="1dthda67a4jn6">{identifier.trim()}</span>.
            </p>

            <div className="mt-6 flex justify-between gap-2" data-id="element-360" data-mp="1irj45day5n97">
              {digits.map((digit, index) => <input key={index} ref={element => {
            codeRefs.current[index] = element;
          }} type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={digit} aria-label={`Chiffre ${index + 1}`} onChange={event => setDigit(index, event.target.value)} onKeyDown={event => {
            if (event.key === 'Backspace' && !digits[index] && index > 0) {
              codeRefs.current[index - 1]?.focus();
            }
          }} className="h-14 w-12 rounded-2xl border border-slate-200 text-center text-xl font-extrabold text-ville-navy transition-colors duration-150 ease-ville focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-361" data-mp="1b8t6gj1hzgpq" data-mpi={index} />)}
            </div>

            {error && <p className="mt-3 text-sm font-semibold text-red-600" data-id="element-362" data-mp="13ohbslhrbjdq">{error}</p>}

            <button type="button" onClick={() => verify(digits)} className={`mt-6 ${primaryButton}`} data-id="element-363" data-mp="13meb97iza7ur">
              Confirmer
            </button>
          </div>}

        {step === 'succes' && <div className="mt-4" data-id="element-364" data-mp="1cn2tkefemudg">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-365" data-mp="1rd0iwxp2lq48">
              <CheckIcon className="h-6 w-6" data-id="element-366" data-mp="20ehvw776lbop" />
            </span>
            <h2 id="auth-titre" className="mt-4 text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-367" data-mp="20doz9z1xs2ka">
              {mode === 'inscription' ? 'Compte créé' : 'Connexion réussie'}
            </h2>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-368" data-mp="60pdfo6yqc4k">Ouverture de vos services…</p>
          </div>}
      </motion.div>
    </div>;
}